import { useState, useRef } from "react";
import { apiRequest, useDatadog } from "@vojomo-core/shared";

interface UseCreateNoteOptions {
  onNoteCreated?: (data: { inserted_id: string; file_name: string }) => void;
}

export const useCreateNote = (options: UseCreateNoteOptions = {}) => {
  const { trackEvent, trackError } = useDatadog();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [noteName, setNoteName] = useState("");
  const [noteContent, setNoteContent] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isNameTaken, setIsNameTaken] = useState(false);

  const checkNameTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const openModal = () => {
    setIsModalOpen(true);
    setNoteName("");
    setNoteContent("");
    setError(null);
    setIsNameTaken(false);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setNoteName("");
    setNoteContent("");
    setError(null);
    setIsNameTaken(false);
  };

  const checkNameExists = async (name: string): Promise<boolean> => {
    try {
      const nameWithExtension = name.endsWith(".txt") ? name : `${name}.txt`;
      const response = await apiRequest<any[]>({
        subRoute: "ai",
        path: `/check_file_name?file_name=${encodeURIComponent(nameWithExtension)}`,
        method: "GET"
      });
      return response.length > 0;
    } catch (error) {
      trackError("error_checking_note_name", error);
      return false;
    }
  };

  const handleNameChange = (value: string) => {
    setNoteName(value);
    setIsNameTaken(false);
    setError(null);

    // Clear any previous timeout
    if (checkNameTimeoutRef.current) {
      clearTimeout(checkNameTimeoutRef.current);
    }

    // Debounce the name check
    if (value && value.trim() !== "") {
      checkNameTimeoutRef.current = setTimeout(async () => {
        const exists = await checkNameExists(value);
        setIsNameTaken(exists);
        if (exists) {
          setError(
            `The name "${value}.txt" is already taken. Please choose another name.`
          );
        }
      }, 500);
    }
  };

  const createNote = async () => {
    if (!noteName.trim() || !noteContent.trim()) {
      setError("Both name and content are required");
      return;
    }

    if (isNameTaken) {
      setError("This name is already taken. Please choose another name.");
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      // Ensure the name has .txt extension
      const nameWithExtension = noteName.endsWith(".txt")
        ? noteName
        : `${noteName}.txt`;

      // Check one more time if the name is taken
      const exists = await checkNameExists(noteName);
      if (exists) {
        setIsNameTaken(true);
        setError(
          `The name "${nameWithExtension}" is already taken. Please choose another name.`
        );
        setIsCreating(false);
        return;
      }

      // Convert content to base64
      const base64Content = btoa(unescape(encodeURIComponent(noteContent)));
      const base64File = `data:text/plain;base64,${base64Content}`;

      trackEvent("creating_note", {
        nameWithExtension,
        fileType: "text",
        noteContent,
        fileSize: base64File.length
      });

      // Upload the note as a text file
      const data = await apiRequest<{
        gcs_path: string;
        inserted_id: string;
      }>({
        subRoute: "ai",
        path: `/upload_file`,
        method: "POST",
        body: {
          base64_file: base64File,
          file_type: "text/plain",
          file_name: nameWithExtension
        }
      });

      // Call success callback if provided
      if (options.onNoteCreated) {
        options.onNoteCreated({
          inserted_id: data.inserted_id,
          file_name: nameWithExtension
        });
      }

      // Close modal and reset
      closeModal();
    } catch (error) {
      trackError("error_creating_note", error);
      setError("Failed to create note. Please try again.");
    } finally {
      setIsCreating(false);
    }
  };

  return {
    isModalOpen,
    noteName,
    noteContent,
    isCreating,
    error,
    isNameTaken,
    openModal,
    closeModal,
    handleNameChange,
    setNoteContent,
    createNote
  };
};
